import { mt, MutationData } from '@passionware/monads';
import { motion } from 'framer-motion';
import { CornerDownLeft } from 'lucide-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ButtonProps } from 'v5/design-system/Button';
import { Kbd } from 'v5/design-system/Kbd';
import { LoadingSpinner } from 'v5/design-system/LoadingSpinner';
import { ModifierKey } from 'v5/primitives/ModifierKey';

export interface FormSaveButtonProps extends Omit<ButtonProps, 'type'> {
  /**
   * Mutation data for handling loading states
   */
  mutation: MutationData<unknown, unknown>;

  /**
   * Save button text (defaults to "Save")
   */
  saveText?: ReactNode;

  showHotKey?: boolean;
}

export function FormSaveButton({
  disabled = false,
  mutation,
  saveText,
  variant = 'primary',
  size = 'md',
  onClick,
  showHotKey = true,
  ...buttonProps
}: FormSaveButtonProps) {
  const { t } = useTranslation('patterns');
  const defaultSaveText = t('FormSaveButton.save');
  const displaySaveText = saveText || defaultSaveText;
  const isDisabled = disabled || mt.isInProgress(mutation);

  return (
    <Button
      visuallyDisabled={isDisabled}
      type="submit"
      variant={variant}
      size={size}
      {...buttonProps}
      onClick={isDisabled ? undefined : onClick}
    >
      <motion.div
        initial={false}
        exit={{ opacity: 0, width: 0 }}
        transition={{ duration: 0.2 }}
        animate={
          mutation && mt.isInProgress(mutation)
            ? { width: 'auto' }
            : { width: 0 }
        }
        className="-mr-3 overflow-hidden"
      >
        <LoadingSpinner className="mr-3" />
      </motion.div>
      {displaySaveText}
      {showHotKey && (
        <Kbd className="-mr-2">
          <ModifierKey />
          <CornerDownLeft />
        </Kbd>
      )}
    </Button>
  );
}
